"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Dropdown } from "primereact/dropdown";
import moment from "moment";

import { useIptuPublico } from "../hooks/useIptuPublico";
import { LoaderBox } from "@/components/LoaderBox";
import { formatoMoeda } from "@/libs/utils/number";
import { TipoGuia, ImovelIPTU, GuiaIPTU } from "@/types";
import { formatCpfCnpj, validarCpfCnpj } from "@/libs/utils/text";
import { InputDropdown } from "@/components/Forms";
import { config } from "@/config";
import { sessionStorage } from "@/libs/sessionStorage";
import { tokensPublico } from "@/libs/cookie";





export default function ConsultaIptu() {
  
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [cpfCnpjValido, setCpfCnpjValido] = useState(false);
  const [imovelSelecionado, setImovelSelecionado] = useState<ImovelIPTU | null>(null);
  const [tipoGuiaSelecionado, setTipoGuiaSelecionado] = useState<TipoGuia | null>(null);
  const [recaptchaValido, setRecaptchaValido] = useState(false);
  const tokenRef = useRef<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    const renderTurnstile = () => {
      const win = window as any;
      if (win.turnstile && turnstileRef.current && !widgetIdRef.current) {
        widgetIdRef.current = win.turnstile.render(turnstileRef.current, {
          sitekey: config.turnstile_key,
          callback: function(token: string) {
            handleRecaptcha(token);
          },
          'expired-callback': function() {
            handleRecaptcha(null);
          },
          'error-callback': function() {
            handleRecaptcha(null);
          }
        });
      }
    };

    const win = window as any;
    if (win.turnstile) {
      renderTurnstile();
    } else {
      let script = document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderTurnstile);
      
      return () => {
        script.removeEventListener('load', renderTurnstile);
      };
    }
  }, []);

  useEffect(() => {
    if (!tokenRef.current) {
      setRecaptchaValido(false);
    }
  }, [tokenRef.current]);

  useEffect(() => {
    if (!recaptchaValido && cpfCnpjValido) {
      handleReset();
    }
  }, [recaptchaValido]);

  const handleReset = () => {
    tokenRef.current = null;
    setCpfCnpj("");
    setCpfCnpjValido(false);
    setImovelSelecionado(null);
    setTipoGuiaSelecionado(null);

    const win = window as any;
    if (win.turnstile && widgetIdRef.current) {
      win.turnstile.reset(widgetIdRef.current);
    }
  };

  const handleRecaptcha = (token: string | null) => {
      tokenRef.current = token;
      setRecaptchaValido(!!token);
  };

  const {
    imoveis,
    guias,
    erro,
    loadingImoveis,
    loadingGuias,
    loadingEmissao,
    buscarImoveis,
    buscarGuias,
    EmitirGuia,
  } = useIptuPublico(handleReset);


  const handleValidarDocumento = useCallback(() => {

    if (!tokenRef.current){
      handleReset()
      return;
    }

    const doc = cpfCnpj.replace(/\D/g, "");
    if (validarCpfCnpj(doc)) {
      setCpfCnpjValido(true);
      buscarImoveis(doc, tokenRef.current);
    } else {
      setCpfCnpjValido(false);
    }
  }, [cpfCnpj, buscarImoveis]);

  const onSelecionarImovel = (imovel: ImovelIPTU | null) => {
    setImovelSelecionado(imovel);
    setTipoGuiaSelecionado(null);
    if (!imovel) {
      return;
    }
  };

  const onSelecionarTipoGuia = (tipo: TipoGuia) => {

    if (!tokenRef.current){
      handleReset()
      return;
    }
    
    setTipoGuiaSelecionado(tipo);
    if (imovelSelecionado) {
      buscarGuias(String(imovelSelecionado.id), tipo);
    }
  };

  const onEmitirGuia = (guiaId: number) => {
    if (!tokenRef.current){
      handleReset()
      return;
    }
    if (!imovelSelecionado) return;
    EmitirGuia(String(guiaId), String(imovelSelecionado.id));
  };

  const getGuiaUnica = (): GuiaIPTU[] => {
    if (guias.length === 0) return [];
    const guiasUnicas = guias
    .filter((g) => g.tipo === "UNICA")
    .sort((a, b) => {
      const data1 = new Date(a.dataVencimento).getTime();
      const data2 = new Date(b.dataVencimento).getTime();
      return data1 - data2;
    });
    return guiasUnicas;
  };
  
  const getGuiasParceladas = (): GuiaIPTU[] => {
    if (guias.length === 0) return [];
    return guias.filter((g) => g.tipo === "PARCELADO");
  };

  const getTotalParcelas = (): number => {
    return getGuiasParceladas().length;
  };

function renderDocumentoHeader() {
  return (
    <div className="flex flex-column gap-3">
      <div className={cpfCnpjValido ? 'hidden' : 'block'}>
        <div className="flex flex-column mb-3">
          <label htmlFor="cpfCnpj" className="text-700 text-sm mb-1">
            CPF ou CNPJ
          </label>
          <InputText
            id="cpfCnpj"
            value={formatCpfCnpj(cpfCnpj)}
            onChange={(e) => setCpfCnpj(e.target.value)}
            placeholder="Digite seu CPF ou CNPJ"
            className="w-full"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleValidarDocumento();
            }}
          />
        </div>
        <div className="justify-content-end flex w-full my-2">
          <div ref={turnstileRef}></div>
        </div>
        <Button label="Consultar" onClick={handleValidarDocumento} className="w-full mb-3" disabled={!recaptchaValido} />

        {erro && (
          <Message severity="warn" text={erro} className="w-full justify-content-start" />
        )}
      </div>

      {cpfCnpjValido && (
        <div className="flex align-items-center gap-2 mb-3 p-3 surface-100 border-round">
          <span className="text-600 text-sm">Contribuinte:</span>
          <span className="text-900 font-medium">
            {formatCpfCnpj(cpfCnpj.replace(/\D/g, ""))}
          </span>
          <Button
            label="Alterar"
            icon="pi pi-pencil"
            size="small"
            text
            onClick={() => {
              setCpfCnpjValido(false);
              setImovelSelecionado(null);
              setTipoGuiaSelecionado(null);
              tokenRef.current = null;
              setRecaptchaValido(false);
              const win = window as any;
              if (win.turnstile && widgetIdRef.current) {
                win.turnstile.reset(widgetIdRef.current);
              }
            }}
            className="ml-auto"
          />
        </div>
      )}
    </div>
  );
}

function renderSeletorImovel() {
  if (!cpfCnpjValido) return null;

  if (loadingImoveis) return <LoaderBox />;

  if (imoveis.length === 0) {
    return (
      <Message
        severity="warn"
        text="Não encontramos nenhum imóvel para o contribuinte informado"
        className="w-full justify-content-start"
      />
    );
  }

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-column">
        <label className="text-700 text-sm mb-1">Selecione um imóvel:</label>
        <InputDropdown
                      name='imovel'
                      label='Imóvel:'
                      options={imoveis}
                      placeholder='Selecionar um Imóvel!'
                      optionValue='id'
                      optionLabel='inscricaoImobiliariaFormatada'
                      filter
                      filterBy='inscricaoImobiliariaFormatada,enderecoFormatado'
                      filterPlaceholder='Pesquisar imóvel...'
                      valueTemplate={(value) => {
                        if (!value) {
                          return 'Selecionar um Imóvel!'
                        } else {
                          return `${value.inscricaoImobiliariaFormatada} - ${value.enderecoFormatado}`
                        }
                      }}
                      itemTemplate={(value) => (`${value.inscricaoImobiliariaFormatada} - ${value.enderecoFormatado}`)}
                      value={imovelSelecionado?.id ?? null}
                      onChange={(value) => {
                        const imovel = imoveis.find(i => i.id === value);
                        if (imovel) onSelecionarImovel(imovel);
                      }}
                    />
      </div>
    </div>
  );
}

function renderSeletorTipoGuia() {
  if (!imovelSelecionado || !cpfCnpjValido) return null;

  return (
    <>
      <hr />
      <div className="text-900 text-lg mb-3 font-medium">
        Selecione o tipo de guia
      </div>
      <div className="grid">
        <div
          className={`surface-card shadow-1 border-round p-4 cursor-pointer hover:shadow-3 transition-duration-200 border-2 col-12 md:col-6 ${
            tipoGuiaSelecionado === "UNICA" ? "border-primary" : "border-transparent"
          }`}
          onClick={() => onSelecionarTipoGuia("UNICA")}
        >
          <div className="flex align-items-center gap-2 mb-2">
            <i className="pi pi-money-bill text-green-500 text-2xl" />
            <span className="font-bold text-900 text-lg">Guia Única</span>
          </div>
          <div className="text-600 text-sm">Pagamento à vista</div>
        </div>

        <div
          className={`surface-card shadow-1 border-round p-4 cursor-pointer hover:shadow-3 transition-duration-200 border-2 col-12 md:col-6 ${
            tipoGuiaSelecionado === "PARCELADO" ? "border-primary" : "border-transparent"
          }`}
          onClick={() => onSelecionarTipoGuia("PARCELADO")}
        >
          <div className="flex align-items-center gap-2 mb-2">
            <i className="pi pi-list text-blue-500 text-2xl" />
            <span className="font-bold text-900 text-lg">Parcelada</span>
            {getTotalParcelas() > 0 && (
              <span className="text-sm text-500 ml-2">
                ({getTotalParcelas()} parcelas)
              </span>
            )}
          </div>
          <div className="text-600 text-sm">Pagamento parcelado</div>
        </div>
      </div>
    </>
  );
}

function renderGuiaCard(guia: GuiaIPTU) {
  return (
    <div
      key={guia.id}
      className="surface-card shadow-2 border-round p-3 flex align-items-center justify-content-between gap-3"
    >
      <div className="flex gap-6">
        <div className="flex flex-column gap-1">
          <span className="text-600 text-sm">Vencimento</span>
          <span className="text-900 font-medium">
            {moment(guia.dataVencimento).format("DD/MM/YYYY")}
          </span>
        </div>
        {guia.percDesconto && (
          <span className="flex text-green-500 text-sm h-full align-items-center justify-content-center font-bold">
            {guia.percDesconto}% de desconto
          </span>
        )}
      </div>
      <div className="flex align-items-center gap-3">
        <div className="flex flex-column gap-1 align-items-end">
          <span className="text-700">
            Total <b>R$ {formatoMoeda(guia.vlLancado - guia.vlDesconto)}</b>
          </span>
        </div>
        <Button
          label="Emitir"
          icon="pi pi-file"
          loading={loadingEmissao}
          onClick={() => onEmitirGuia(guia.id)}
        />
      </div>
    </div>
  );
}

function renderParcelaCard(guia: GuiaIPTU) {
  return (
    <div
      key={guia.id}
      className="surface-card shadow-2 border-round p-3 flex align-items-center justify-content-between gap-3"
    >
      <div className="flex gap-6">
        <div className="flex flex-column gap-1">
          <span className="text-600 text-sm">Parcela {guia.parcela}</span>
          <span className="text-900 font-medium">
            {moment(guia.dataVencimento).format("DD/MM/YYYY")}
          </span>
        </div>
      </div>
      <div className="flex align-items-center gap-3">
        <span className="text-700">
          <b>R$ {formatoMoeda(guia.vlLancado - guia.vlDesconto)}</b>
        </span>
        <Button
          label="Emitir"
          icon="pi pi-file"
          size="small"
          loading={loadingEmissao}
          onClick={() => onEmitirGuia(guia.id)}
        />
      </div>
    </div>
  );
}

function renderGuiasUnicas() {
  const guia = getGuiaUnica();

  if (!guia.length) {
    return (
      <Message
        severity="warn"
        text="Não há guia única disponível para este imóvel"
        className="w-full justify-content-start"
      />
    );
  }

  return (
    <div className="flex flex-column gap-2">
      {guias.map(renderGuiaCard)}
    </div>
  );
}

function renderGuiasParceladas() {
  const parceladas = getGuiasParceladas();

  if (parceladas.length === 0) {
    return (
      <Message
        severity="warn"
        text="Não há guias parceladas disponíveis para este imóvel"
        className="w-full justify-content-start"
      />
    );
  }

  return (
    <div className="flex flex-column gap-2">
      {parceladas.map(renderParcelaCard)}
    </div>
  );
}

function renderEstadoGuias() {
  if (loadingGuias) return <LoaderBox />;
  if (erro) return <Message severity="warn" text={erro} className="w-full justify-content-start" />;
  if (guias.length === 0) return <Message severity="warn" text="Não há guias disponíveis para este imóvel" className="w-full justify-content-start" />;
  return null;
}

function renderGuias() {
  if (!tipoGuiaSelecionado) return null;

  const estado = renderEstadoGuias();

  return (
    <>
      <hr />
      {estado && estado}
      {!estado && tipoGuiaSelecionado === "UNICA" && renderGuiasUnicas()}
      {!estado && tipoGuiaSelecionado === "PARCELADO" && renderGuiasParceladas()}
    </>
  );
}

// ─── JSX principal ──────────────────────────────────────────────────────────

return (
  <div className="col-12">
    <div className="text-900 text-xl mb-3 font-medium text-center">
      Emissão de Guias - IPTU
    </div>
    {renderDocumentoHeader()}
    {renderSeletorImovel()}
    {renderSeletorTipoGuia()}
    {renderGuias()}
  </div>
);
}